import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy, ExternalLink, Eye, Link2, PenLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { FORMATS, baseTitle } from '@/lib/bracket/templates';
import { shareApi, shareUrl } from '@/lib/bracket/shareApi';
import { fmtDate } from '@/lib/billingApi';

const ShareRow = ({ s, onEdit }) => {
  const url = shareUrl(s.id);
  const copy = () => navigator.clipboard.writeText(url).then(() => toast.success('Link copied')).catch(() => toast.error('Copy failed'));
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-3 hover:shadow-md transition-shadow" data-testid={`my-share-${s.id}`}>
      <div className="min-w-0">
        <h3 className="font-bold text-[#00247D] truncate" style={{ fontFamily: 'Barlow Condensed' }}>{s.title || baseTitle(s.num_teams, s.format)}</h3>
        <p className="text-xs text-slate-500">{s.num_teams} teams · {FORMATS[s.format]?.label}</p>
        <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-2">Updated {fmtDate(s.updated_at)}<span className="inline-flex items-center gap-0.5"><Eye className="w-3 h-3" />{s.views || 0}</span></p>
      </div>
      <div className="flex gap-1.5">
        <Button size="sm" className="flex-1 bg-[#00247D] hover:bg-[#001A5C]" onClick={() => onEdit(s)} data-testid={`my-share-edit-${s.id}`}><PenLine className="w-3.5 h-3.5 mr-1" />Edit</Button>
        <Button size="sm" variant="outline" onClick={copy} aria-label="Copy link" data-testid={`my-share-copy-${s.id}`}><Copy className="w-3.5 h-3.5" /></Button>
        <a href={url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" aria-label="Open" data-testid={`my-share-open-${s.id}`}><ExternalLink className="w-3.5 h-3.5" /></Button></a>
      </div>
    </div>
  );
};

// Signed-in organizers see their shared brackets above the template grid, on any device.
export const MyShares = ({ onEdit }) => {
  const { user } = useAuth();
  const [shares, setShares] = useState([]);
  useEffect(() => {
    if (!user) { setShares([]); return; }
    shareApi.mine().then((r) => setShares(r.shares)).catch(() => setShares([]));
  }, [user]);
  if (!shares.length) return null;
  return (
    <section className="mb-8 animate-in fade-in duration-300" data-testid="my-shares">
      <h2 className="text-sm font-bold uppercase tracking-[0.14em] text-slate-500 mb-3 flex items-center gap-2"><Link2 className="w-4 h-4 text-[#CF142B]" />Your shared brackets</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {shares.map((s) => <ShareRow key={s.id} s={s} onEdit={onEdit} />)}
      </div>
    </section>
  );
};
